import { useState, useEffect, useRef } from "react";
import { Link } from "react-router";
import { ChevronLeft, ChevronRight, ShoppingBag, Heart } from "lucide-react";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import { useCart } from "./CartContext";
import { useWishlist } from "./WishlistContext";
import { useAdminData, type AdminProduct } from "./admin/AdminDataContext";
import { localized } from "./admin/TranslationsPanel";
import { useTheme } from "./ThemeInjector";
import { useLanguage } from "./LanguageContext";
import { isSupabaseConnected, fetchProducts } from "./supabase";
import { toast } from "sonner";
import type { I18nMap } from "./admin/TranslationsPanel";

const tagStyles: Record<string, string> = {
  new: "bg-[#6F7F63] text-white",
  limited: "bg-[#C7A76C] text-white",
  "sold out": "bg-[#6F675F] text-white",
};

function mapRowToProduct(row: {
  id: number;
  slug: string;
  name: string;
  price: number;
  category: string;
  tag?: string | null;
  description?: string | null;
  details?: unknown;
  images?: string[] | null;
  related_ids?: number[] | null;
  stock_quantity?: number;
  low_stock_threshold?: number;
  i18n?: Record<string, unknown> | null;
}): AdminProduct {
  return {
    id: row.id,
    slug: row.slug,
    name: row.name,
    price: Number(row.price),
    category: row.category,
    tag: (row.tag as AdminProduct["tag"]) ?? "",
    description: row.description ?? "",
    details: Array.isArray(row.details) ? (row.details as { label: string; value: string }[]) : [],
    images: Array.isArray(row.images) ? row.images : [],
    relatedIds: Array.isArray(row.related_ids) ? row.related_ids : [],
    stock_quantity: row.stock_quantity ?? 0,
    low_stock_threshold: row.low_stock_threshold ?? 5,
    i18n: (row.i18n as I18nMap) ?? {},
  };
}

const CARD_MIN_WIDTH = 280;

export function ProductCarouselSection() {
  const { products: contextProducts, categories: contextCategories, siteConfig } = useAdminData();
  const wishlistEnabled = siteConfig.features?.wishlist !== false;
  const { lang, t } = useLanguage();
  const theme = useTheme();
  const { addItem } = useCart();
  const { isWishlisted, toggleWishlist } = useWishlist();
  const scrollRef = useRef<HTMLDivElement>(null);

  const [products, setProducts] = useState<AdminProduct[]>(() =>
    isSupabaseConnected() ? [] : contextProducts
  );
  const [loading, setLoading] = useState(isSupabaseConnected());
  const ALL_FILTER_KEY = "__all__";
  const [activeFilter, setActiveFilter] = useState(ALL_FILTER_KEY);

  useEffect(() => {
    if (!isSupabaseConnected()) {
      setProducts(contextProducts);
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchProducts()
      .then(({ data, error }) => {
        if (error) {
          console.warn("[MOECHA] Failed to fetch products:", error.message);
          setProducts(contextProducts);
          return;
        }
        setProducts((data ?? []).map((r) => mapRowToProduct(r as Parameters<typeof mapRowToProduct>[0])));
      })
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isSupabaseConnected()) return;
    setProducts(contextProducts);
  }, [contextProducts]);

  const categories = isSupabaseConnected()
    ? [...new Set(products.map((p) => p.category))]
    : contextCategories;
  const filterOptions = [
    { id: ALL_FILTER_KEY, label: t("products.all") },
    ...categories.map((c) => ({ id: c, label: c })),
  ];
  const filtered =
    activeFilter === ALL_FILTER_KEY
      ? products
      : products.filter((p) => p.category === activeFilter);

  const scroll = (direction: "left" | "right") => {
    const el = scrollRef.current;
    if (!el) return;
    const step = el.clientWidth * 0.85;
    const target = el.scrollLeft + (direction === "left" ? -step : step);
    el.scrollTo({ left: Math.max(0, target), behavior: "smooth" });
  };

  const handleAddToCart = (product: AdminProduct, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (product.stock_quantity === 0) return;
    addItem({
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.images[0],
    });
    toast.success(`${localized(product, "name", lang)} ${t("products.added")}`);
  };

  const handleWishlistToggle = (product: AdminProduct, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggleWishlist(product.id);
    toast.success(
      isWishlisted(product.id) ? t("wishlist.removed") : t("wishlist.added")
    );
  };

  if (loading || products.length === 0) {
    if (loading) {
      return (
        <section id="collection" className="py-24" style={{ backgroundColor: `${theme.border}30` }}>
          <div className="max-w-4xl mx-auto px-6 text-center">
            <p style={{ fontFamily: theme.fontBody, fontSize: "14px", fontWeight: 300, color: theme.muted }}>Loading…</p>
          </div>
        </section>
      );
    }
    return null;
  }

  return (
    <section id="collection" className="py-24 overflow-hidden" style={{ backgroundColor: `${theme.border}30` }}>
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <p
            className="tracking-[0.3em] uppercase mb-2"
            style={{ fontFamily: theme.fontBody, fontSize: "12px", color: theme.accent }}
          >
            {t("products.label")}
          </p>
          <h2
            className="tracking-[0.08em]"
            style={{ fontFamily: theme.fontHeading, fontSize: "clamp(26px, 3.5vw, 38px)", fontWeight: 400, color: theme.fg }}
          >
            {t("products.title")}
          </h2>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap justify-center gap-3 mb-10" role="group" aria-label="Product category filters">
          {filterOptions.map((opt) => (
            <button
              key={opt.id}
              onClick={() => setActiveFilter(opt.id)}
              className="px-5 py-2 rounded-full tracking-[0.15em] uppercase transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#C7A76C] focus:ring-offset-2"
              style={{
                fontFamily: theme.fontBody,
                fontSize: "11px",
                fontWeight: 400,
                backgroundColor: activeFilter === opt.id ? theme.primary : "transparent",
                color: activeFilter === opt.id ? theme.btnText : theme.muted,
              }}
              aria-pressed={activeFilter === opt.id}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Horizontal scrollable product row */}
        <div className="relative">
          <div
            ref={scrollRef}
            className="flex gap-8 overflow-x-auto overflow-y-hidden pb-4 scroll-smooth scrollbar-thin"
            style={{
              scrollSnapType: "x proximity",
              scrollbarWidth: "thin",
              WebkitOverflowScrolling: "touch",
            }}
            role="list"
          >
            {filtered.map((product) => (
              <div
                key={product.id}
                className="shrink-0"
                style={{
                  minWidth: CARD_MIN_WIDTH,
                  maxWidth: CARD_MIN_WIDTH,
                  scrollSnapAlign: "start",
                }}
                role="listitem"
              >
                <Link
                  to={`/product/${product.slug}`}
                  className="group block"
                  aria-label={`${localized(product, "name", lang)} - €${product.price}`}
                >
                  <div className="relative overflow-hidden rounded-xl mb-5 bg-[#F2EEE8]">
                    <ImageWithFallback
                      src={product.images[0]}
                      alt={localized(product, "name", lang)}
                      className={`w-full aspect-[3/4] object-cover group-hover:scale-[1.03] transition-transform duration-700 ease-out ${
                        product.stock_quantity === 0 ? "opacity-50" : ""
                      }`}
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-all duration-500" />

                    {product.tag && (
                      <span
                        className={`absolute top-3 left-3 px-3 py-1 rounded-full tracking-[0.15em] uppercase ${tagStyles[product.tag]}`}
                        style={{ fontFamily: theme.fontBody, fontSize: "10px" }}
                      >
                        {product.tag}
                      </span>
                    )}

                    {product.stock_quantity > 0 &&
                      product.stock_quantity <= product.low_stock_threshold &&
                      !product.tag && (
                        <span
                          className="absolute top-3 left-3 px-3 py-1 rounded-full tracking-[0.15em] uppercase bg-[#C7A76C]/90 text-white"
                          style={{ fontFamily: theme.fontBody, fontSize: "10px" }}
                        >
                          {t("products.low_stock")}
                        </span>
                      )}

                    {product.stock_quantity === 0 && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white/40">
                        <span
                          className="px-4 py-2 bg-[#2D2A26]/80 text-white rounded-full tracking-[0.2em] uppercase"
                          style={{ fontFamily: theme.fontBody, fontSize: "11px" }}
                        >
                          {t("products.sold_out")}
                        </span>
                      </div>
                    )}

                    {wishlistEnabled && (
                      <button
                        onClick={(e) => handleWishlistToggle(product, e)}
                        className={`absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#C7A76C] ${
                          isWishlisted(product.id)
                            ? "bg-[#8A6D63] text-white shadow-md"
                            : "bg-white/80 backdrop-blur-sm text-[#6F675F] opacity-0 group-hover:opacity-100"
                        }`}
                        aria-label={isWishlisted(product.id) ? "Remove from wishlist" : "Add to wishlist"}
                      >
                        <Heart
                          size={14}
                          className={isWishlisted(product.id) ? "fill-white" : ""}
                        />
                      </button>
                    )}

                    {product.stock_quantity > 0 && (
                      <button
                        onClick={(e) => handleAddToCart(product, e)}
                        className="absolute bottom-3 right-3 w-10 h-10 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center text-[#2D2A26] opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300 hover:bg-[#6F7F63] hover:text-white shadow-[0_8px_30px_rgba(40,32,20,0.06)] focus:outline-none focus:ring-2 focus:ring-[#C7A76C]"
                        aria-label={`${t("products.add_to_cart")} ${localized(product, "name", lang)}`}
                      >
                        <ShoppingBag size={16} />
                      </button>
                    )}
                  </div>
                  <h3
                    className="tracking-[0.08em] mb-1"
                    style={{ fontFamily: theme.fontHeading, fontSize: "18px", fontWeight: 500, color: theme.fg }}
                  >
                    {localized(product, "name", lang)}
                  </h3>
                  <p
                    className="tracking-[0.1em]"
                    style={{ fontFamily: theme.fontBody, fontSize: "14px", fontWeight: 300, color: theme.muted }}
                  >
                    &euro;{product.price}
                  </p>
                </Link>
              </div>
            ))}
          </div>

          {/* Prev/Next scroll buttons */}
          {filtered.length > 0 && (
            <div className="flex items-center justify-center gap-4 mt-6">
              <button
                type="button"
                onClick={() => scroll("left")}
                aria-label="Scroll left"
                className="flex items-center justify-center w-12 h-12 rounded-full border transition-all duration-300 hover:scale-[1.02]"
                style={{ borderColor: theme.border, color: theme.fg, backgroundColor: theme.card }}
              >
                <ChevronLeft size={22} strokeWidth={1.5} />
              </button>
              <button
                type="button"
                onClick={() => scroll("right")}
                aria-label="Scroll right"
                className="flex items-center justify-center w-12 h-12 rounded-full border transition-all duration-300 hover:scale-[1.02]"
                style={{ borderColor: theme.border, color: theme.fg, backgroundColor: theme.card }}
              >
                <ChevronRight size={22} strokeWidth={1.5} />
              </button>
            </div>
          )}
        </div>

        <div className="text-center mt-8">
          <Link
            to="/shop"
            className="inline-block tracking-[0.2em] uppercase transition-opacity hover:opacity-80"
            style={{ fontFamily: theme.fontBody, fontSize: "11px", color: theme.primary }}
          >
            {t("products.see_all")}
          </Link>
        </div>
      </div>
    </section>
  );
}
